import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EmailAttachmentPreview, EmailManagementApiService, EmailPreviewVersion } from '@anduin/api';
import {
  ButtonDirective,
  DataSelectorDirective,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogSize,
  IconComponent,
  Logger,
  openUrlInNewTab,
  TooltipDirective,
} from '@anduin/shared';
import { finalize, take } from 'rxjs';
import { OutboundEmailRow } from '../../models/outbound-emails.model';

export interface ViewAttachmentsDialogData {
  model: OutboundEmailRow;
}

@Component({
  selector: 'app-view-attachments-dialog',
  templateUrl: './view-attachments-dialog.component.html',
  styleUrl: './view-attachments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    DataSelectorDirective,
    DialogComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    IconComponent,
    MatProgressSpinnerModule,
    TooltipDirective,
  ],
})
export class ViewAttachmentsDialogComponent {
  private readonly data: ViewAttachmentsDialogData = inject(MAT_DIALOG_DATA);
  private readonly emailManagementApiService = inject(EmailManagementApiService);
  private readonly logger = inject(Logger);

  private readonly emailId = this.data.model?.id ?? this.data.model?.outboundEmailId ?? null;
  private readonly emailVersion: EmailPreviewVersion = this.data.model?.id ? 'v4' : 'v3';

  readonly isLoading = signal<boolean>(false);
  readonly loadErrorMessage = signal<string | null>(null);
  readonly downloadErrorMessage = signal<string | null>(null);
  readonly attachments = signal<EmailAttachmentPreview[]>([]);
  readonly downloadingFileId = signal<number | null>(null);

  protected readonly DialogSize = DialogSize;

  constructor() {
    this._loadAttachments();
  }

  downloadAttachment(attachment: EmailAttachmentPreview): void {
    if (this.downloadingFileId() !== null) {
      return;
    }
    this.downloadErrorMessage.set(null);
    this.downloadingFileId.set(attachment.fileId);
    this.emailManagementApiService
      .getEmailAttachmentDownloadUrl(this.emailId!, attachment.fileId, this.emailVersion)
      .pipe(
        take(1),
        finalize(() => this.downloadingFileId.set(null)),
      )
      .subscribe({
        next: (result) => {
          if (result?.url) {
            openUrlInNewTab(result.url);
          } else {
            this.downloadErrorMessage.set(`A download link for ${attachment.filename} is not available.`);
          }
        },
        error: () => this.downloadErrorMessage.set(`Failed to download ${attachment.filename}. Please try again.`),
      });
  }

  private _loadAttachments(): void {
    if (this.emailId === null) {
      this.loadErrorMessage.set('Attachments are not available for this email.');
      return;
    }

    this.isLoading.set(true);
    this.emailManagementApiService
      .getEmailAttachments(this.emailId, this.emailVersion)
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: (attachments) => this.attachments.set(attachments),
        error: (error) => {
          this.logger.error('ViewAttachmentsDialogComponent :: Failed to load the email attachments', error);
          this.loadErrorMessage.set('Failed to load attachments. Please try again.');
        },
      });
  }
}
